import i18n from '~/services/i18n';

const PAGE_SIZE = 10;

const ORDER = {
  ASC: 'asc',
  DESC: 'desc',
};

const SORT_BY = {
  ALPHABETICAL: 'alphabetical',
  CREATED_AT: 'createdAt',
};

const CONTENT_TYPE = {
  QUIZ: 'quiz',
  PRESENTATION: 'presentation',
  ALL: 'all',
  VIDEO_QUIZ: 'video-quiz',
  READING_QUIZ: 'reading-quiz',
};

const QUIZ_STATUS: Record<'PUBLISHED' | 'DRAFT' | 'LIFETIME_ACCESS' | 'ARCHIVED', 'published' | 'draft' | 'lifetimeAccess' | 'archived'> = {
  PUBLISHED: 'published',
  DRAFT: 'draft',
  LIFETIME_ACCESS: 'lifetimeAccess',
  ARCHIVED: 'archived',
};

const contentTypeToValueMap = {
  [CONTENT_TYPE.ALL]: [],
  [CONTENT_TYPE.PRESENTATION]: [CONTENT_TYPE.PRESENTATION],
  [CONTENT_TYPE.QUIZ]: [CONTENT_TYPE.QUIZ],
  [CONTENT_TYPE.VIDEO_QUIZ]: [CONTENT_TYPE.VIDEO_QUIZ],
  [CONTENT_TYPE.READING_QUIZ]: [CONTENT_TYPE.READING_QUIZ],
};

const FILTER_KEYS = {
  ALL_QUIZZES: 'allQuizzes',
  CREATED_BY_ME: 'createdByMe', // QUIZ_STATUSES
  HOSTED_QUIZZES: 'hostedQuizzes',
  LIKED_QUIZZES: 'likedQuizzes',
  SHARED_WITH_ME: 'sharedWithMe',
  DRAFT_QUIZZES: 'draftQuizzes', // deprecated for my library v2
  IMPORTED: 'imported',
  STANDARD_TAGGING: 'standardTagging',
  COLLECTIONS: 'collections',
  TEAMS: 'teams',
};

const SUB_FILTER_KEYS = {
  // query params
  ORDER: 'order',
  SORT_BY: 'sortBy',
  CONTENT_TYPE: 'contentType',
  QUIZ_STATUS: 'quizStatus',
  TERM: 'term',
  // path params
  TEAM_ID: 'teamId',
  COLLECTION_ID: 'collectionId',

  // generic placeholder for entries in FILTER_KEYS, being used in code-side, can be reiterated
  FILTER_ID: 'filterId',
};

const commonFilterConfig = {
  [SUB_FILTER_KEYS.ORDER]: {
    default: ORDER.DESC,
    options: Object.values(ORDER),
  },
  [SUB_FILTER_KEYS.SORT_BY]: {
    default: SORT_BY.CREATED_AT,
    options: Object.values(SORT_BY),
  },
  [SUB_FILTER_KEYS.CONTENT_TYPE]: {
    default: CONTENT_TYPE.ALL,
    options: Object.values(CONTENT_TYPE),
  },
  [SUB_FILTER_KEYS.QUIZ_STATUS]: {
    default: QUIZ_STATUS.PUBLISHED,
    options: Object.values(QUIZ_STATUS),
  },
  [SUB_FILTER_KEYS.TERM]: {
    options: [],
    default: null,
  },
};

const commonAllowedFilters = [SUB_FILTER_KEYS.ORDER, SUB_FILTER_KEYS.SORT_BY, SUB_FILTER_KEYS.CONTENT_TYPE];

const allowedQueryFilterKeyMap = {
  [FILTER_KEYS.CREATED_BY_ME]: [...commonAllowedFilters, SUB_FILTER_KEYS.QUIZ_STATUS, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.HOSTED_QUIZZES]: [...commonAllowedFilters, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.ALL_QUIZZES]: [...commonAllowedFilters, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.LIKED_QUIZZES]: [...commonAllowedFilters, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.SHARED_WITH_ME]: [...commonAllowedFilters, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.DRAFT_QUIZZES]: [...commonAllowedFilters, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.IMPORTED]: commonAllowedFilters,
  [FILTER_KEYS.STANDARD_TAGGING]: [...commonAllowedFilters, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.COLLECTIONS]: [...commonAllowedFilters, SUB_FILTER_KEYS.TERM],
  [FILTER_KEYS.TEAMS]: commonAllowedFilters,
};

const rootPath = '/admin/my-library';

const filterKeyToPathMap = {
  [FILTER_KEYS.CREATED_BY_ME]: `${rootPath}/${FILTER_KEYS.CREATED_BY_ME}`,
  [FILTER_KEYS.HOSTED_QUIZZES]: `${rootPath}/${FILTER_KEYS.HOSTED_QUIZZES}`,
  [FILTER_KEYS.ALL_QUIZZES]: `${rootPath}/${FILTER_KEYS.ALL_QUIZZES}`,
  [FILTER_KEYS.LIKED_QUIZZES]: `${rootPath}/${FILTER_KEYS.LIKED_QUIZZES}`,
  [FILTER_KEYS.SHARED_WITH_ME]: `${rootPath}/${FILTER_KEYS.SHARED_WITH_ME}`,
  [FILTER_KEYS.DRAFT_QUIZZES]: `${rootPath}/${FILTER_KEYS.DRAFT_QUIZZES}`,
  [FILTER_KEYS.IMPORTED]: `${rootPath}/${FILTER_KEYS.IMPORTED}`,
  [FILTER_KEYS.STANDARD_TAGGING]: `${rootPath}/${FILTER_KEYS.STANDARD_TAGGING}`,
  [FILTER_KEYS.COLLECTIONS]: `${rootPath}/${FILTER_KEYS.COLLECTIONS}`,
  [FILTER_KEYS.TEAMS]: `${rootPath}/${FILTER_KEYS.TEAMS}`,
};

// const genericUrlPath = `${rootPath}/:${SUB_FILTER_KEYS.FILTER_ID}`;

const filterSet = new Set(Object.keys(allowedQueryFilterKeyMap));

const contentStatusCountParamsMap: Record<string, keyof ContentCounts> = {
  // flat library filter
  [FILTER_KEYS.CREATED_BY_ME]: 'publishedQuizzes',
  [FILTER_KEYS.IMPORTED]: 'imported',
  [FILTER_KEYS.HOSTED_QUIZZES]: 'hostedQuizzes',
  [FILTER_KEYS.LIKED_QUIZZES]: 'likedQuizzes',
  [FILTER_KEYS.SHARED_WITH_ME]: 'sharedQuizzes',
  // [FILTER_KEYS.DRAFT_QUIZZES]: 'draftQuizzes',
  [FILTER_KEYS.STANDARD_TAGGING]: 'standardTaggedQuizzes',
  [FILTER_KEYS.ALL_QUIZZES]: 'totalQuizzes',

  // for created by me
  [QUIZ_STATUS.DRAFT]: 'draftQuizzes',
  [QUIZ_STATUS.PUBLISHED]: 'publishedQuizzes',
  [QUIZ_STATUS.ARCHIVED]: 'archivedQuizzes',
  [QUIZ_STATUS.LIFETIME_ACCESS]: 'lifetimeAccessQuizzes',
};

interface ContentCounts {
  createdQuizzes: number,
  imported: number,
  hostedQuizzes: number,
  likedQuizzes: number,
  sharedQuizzes: number,
  standardTaggedQuizzes: number,
  totalQuizzes: number,
  draftQuizzes: number,
  publishedQuizzes: number,
  archivedQuizzes: number,
  lifetimeAccessQuizzes: number,
}

export const LIBRARY_FILTER_ITEMS = {
  [FILTER_KEYS.ALL_QUIZZES]: {
    searchParam: FILTER_KEYS.ALL_QUIZZES,
    id: FILTER_KEYS.ALL_QUIZZES,
    title: i18n('All my content'),
    icon: 'fal fa-archive',
    activeIcon: 'fas fa-archive',
    countField: contentStatusCountParamsMap[FILTER_KEYS.ALL_QUIZZES],
  },
  [FILTER_KEYS.CREATED_BY_ME]: {
    searchParam: FILTER_KEYS.CREATED_BY_ME,
    id: FILTER_KEYS.CREATED_BY_ME,
    title: i18n('Created by me'),
    icon: 'fal fa-user',
    activeIcon: 'fas fa-user',
    countField: contentStatusCountParamsMap[FILTER_KEYS.CREATED_BY_ME],
    showLimitTooltip: true,
  },
  [FILTER_KEYS.IMPORTED]: {
    searchParam: FILTER_KEYS.IMPORTED,
    id: FILTER_KEYS.IMPORTED,
    title: i18n('Imported'),
    icon: 'fal fa-file-import',
    activeIcon: 'fas fa-file-import',
    countField: contentStatusCountParamsMap[FILTER_KEYS.IMPORTED],
    isNew: true,
  },
  [FILTER_KEYS.HOSTED_QUIZZES]: {
    searchParam: FILTER_KEYS.HOSTED_QUIZZES,
    id: FILTER_KEYS.HOSTED_QUIZZES,
    title: i18n('Previously used'),
    icon: 'fal fa-history',
    activeIcon: 'fas fa-history',
    countField: contentStatusCountParamsMap[FILTER_KEYS.HOSTED_QUIZZES],
  },
  [FILTER_KEYS.LIKED_QUIZZES]: {
    searchParam: '__love__',
    id: FILTER_KEYS.LIKED_QUIZZES,
    title: i18n('Liked by me'),
    icon: 'fal fa-heart',
    activeIcon: 'fas fa-heart',
    countField: contentStatusCountParamsMap[FILTER_KEYS.LIKED_QUIZZES],
  },
  [FILTER_KEYS.SHARED_WITH_ME]: {
    searchParam: FILTER_KEYS.SHARED_WITH_ME,
    id: FILTER_KEYS.SHARED_WITH_ME,
    title: i18n('Shared with me'),
    icon: 'fal fa-users',
    activeIcon: 'fas fa-users',
    countField: contentStatusCountParamsMap[FILTER_KEYS.SHARED_WITH_ME],
  },
  [FILTER_KEYS.DRAFT_QUIZZES]: {
    searchParam: FILTER_KEYS.DRAFT_QUIZZES,
    id: FILTER_KEYS.DRAFT_QUIZZES,
    title: i18n('Drafts'),
    icon: 'fal fa-file-alt',
    activeIcon: 'fas fa-file-alt',
    countField: contentStatusCountParamsMap[FILTER_KEYS.DRAFT_QUIZZES],
  },
  [FILTER_KEYS.STANDARD_TAGGING]: {
    searchParam: FILTER_KEYS.STANDARD_TAGGING,
    id: FILTER_KEYS.STANDARD_TAGGING,
    title: i18n('Standards Tagged'),
    icon: 'fal fa-tags',
    activeIcon: 'fas fa-tags',
    countField: contentStatusCountParamsMap[FILTER_KEYS.STANDARD_TAGGING],
  },
};

const MY_LIBRARY_FILTERS_LIST = [
  LIBRARY_FILTER_ITEMS[FILTER_KEYS.CREATED_BY_ME],
  LIBRARY_FILTER_ITEMS[FILTER_KEYS.IMPORTED],
  LIBRARY_FILTER_ITEMS[FILTER_KEYS.HOSTED_QUIZZES],
  LIBRARY_FILTER_ITEMS[FILTER_KEYS.LIKED_QUIZZES],
  LIBRARY_FILTER_ITEMS[FILTER_KEYS.SHARED_WITH_ME],
  // LIBRARY_FILTER_ITEMS[FILTER_KEYS.DRAFT_QUIZZES],
  LIBRARY_FILTER_ITEMS[FILTER_KEYS.STANDARD_TAGGING],
  LIBRARY_FILTER_ITEMS[FILTER_KEYS.ALL_QUIZZES],
];

const LIBRARY_LIMITS_FEATURE_NAME = 'library-limits';
const UPSELL_VARIANT_ARCHIVED_RESOURCE = 'archived-resource';
const UPSELL_VARIANT_ARCHIVED_TITLE = i18n('Regain complete access to your archived activities');

const USAGE_INFO_MODE = {
  ERROR: 'error',
  WARNING: 'warning',
  NORMAL: 'normal',
};

export {
  FILTER_KEYS,
  MY_LIBRARY_FILTERS_LIST,
  CONTENT_TYPE,
  ORDER,
  SORT_BY,
  QUIZ_STATUS,
  SUB_FILTER_KEYS,
  PAGE_SIZE,
  // genericUrlPath,

  filterSet,
  filterKeyToPathMap,
  commonFilterConfig,
  allowedQueryFilterKeyMap,
  contentTypeToValueMap,
  contentStatusCountParamsMap,

  LIBRARY_LIMITS_FEATURE_NAME,
  UPSELL_VARIANT_ARCHIVED_RESOURCE,
  UPSELL_VARIANT_ARCHIVED_TITLE,
  USAGE_INFO_MODE,
};

export type { ContentCounts };

export const CONTENT_LIMIT_BANNER_TYPE = {
  LIMIT_EXCEEDED: 'limit-exceeded',
  LIMIT_WARNING: 'limit-warning',
};
